import { CoachCard } from '@efdigital/ll-addon-coaching';
import { getStatusIcon } from '@efdigital/ll-addon-coaching/utils/coaching-sessions/coach-partnership-status';
import type { CoachingPartnership } from '@efdigital/ll-addon-data';
import { Heading } from '@efdigital/ll-addon-ui';
import { CoachPartnershipActions } from '@efdigital/ll-xceed-v2/components/coaching/coachee/coach-partnership-actions.gts';
import type CoachingCoacheeCohortCoachesIndexController from '@efdigital/ll-xceed-v2/controllers/coaching/coachee/cohort/coaches/index';
import { queryCoachProfilesByPartnerships } from '@efdigital/ll-xceed-v2/data/builders/coaching/coach-profiles';
import type { CoachingCoacheeCohortModel } from '@efdigital/ll-xceed-v2/routes/coaching/coachee/cohort';
import { array } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import Component from '@glimmer/component';
import { Request } from '@warp-drive/ember';
import { pageTitle } from 'ember-page-title';

interface CoachesShortlistSignature {
  Args: {
    model: CoachingCoacheeCohortModel;
    controller: CoachingCoacheeCohortCoachesIndexController;
  };
}

const None = <template>
  <div class="alert alert-primary">
    <p>You have not shortlisted any coaches yet.</p>
    <LinkTo
      @route="coaching.coachee.cohort.coaches.index"
      class="btn btn-primary"
    >Browse Coaches</LinkTo>
  </div>
</template>;

function moreThanOne(count: number): boolean {
  return count > 1;
}

export default class CoachesShortlist extends Component<CoachesShortlistSignature> {
  queryShortlist(partnerships: CoachingPartnership[]) {
    return queryCoachProfilesByPartnerships(partnerships);
  }

  <template>
    {{pageTitle "Shortlist"}}

    <div class="container my-3">
      {{#if @model.planner.coachHasBeenChosen}}
        <Heading
          @size="sm"
          @text="Chemistry Matching Complete"
          @secondary="You have completed the chemistry matching process and have chosen a coach for your coaching journey."
          class="mb-4"
        />
      {{else}}
        <Heading
          @size="sm"
          @text="Chemistry Matching"
          @secondary="Once you have shortlisted a coach, you can book a chemistry session with them to decide if they are the right fit for you."
          class="mb-4"
        />

        <div class="alert alert-primary">
          {{#if (moreThanOne @model.planner.quota.chemistry.remaining)}}
            <p>You can book up to
              {{@model.planner.quota.chemistry.remaining}}
              more chemistry sessions.</p>
          {{else if @model.planner.quota.chemistry.remaining}}
            <p>You can book one more chemistry session.</p>
          {{else}}
            <p>You have reached the maximum number of chemistry sessions. You
              must now decide which coach you want to work with.</p>
          {{/if}}
          <p class="mb-0">Once you have completed a chemistry session, you
            confirm your coach and start your coaching journey with them.</p>
        </div>
      {{/if}}

      {{#if @model.planner.chemistryAndChosenPartnerships.length}}
        <Request
          @query={{this.queryShortlist
            @model.planner.chemistryAndChosenPartnerships
          }}
        >
          <:content as |coachProfiles|>
            {{#if coachProfiles.data.length}}
              <div class="row g-3 g-xl-4 align-items-stretch">
                {{#each coachProfiles.data as |coachProfile|}}

                  {{#let
                    (@model.planner.getStatusForCoach coachProfile.user)
                    as |status|
                  }}
                    {{#let
                      (@model.planner.hasUncancelledChemistrySession
                        coachProfile.user
                      )
                      as |hasChemistrySession|
                    }}
                      {{#let
                        (getStatusIcon status hasChemistrySession)
                        as |icon|
                      }}
                        <div class="col-12 col-md-6 col-xxl-4">

                          <CoachCard
                            class="h-100 w-100"
                            @coach={{coachProfile}}
                            @cardClass="h-100 w-100 rounded-4 shadow-sm"
                            @footerClass="d-flex flex-row justify-content-between gap-1 align-items-center text-bg-white"
                            @color={{icon.color}}
                          >
                            <:badge>
                              {{#if icon}}
                                <span
                                  class="badge rounded-pill bg-{{icon.color}}-subtle text-dark"
                                >
                                  <iconify-icon icon="{{icon.icon}}" inline />
                                  {{icon.label}}
                                </span>
                              {{/if}}
                            </:badge>
                            <:actionsBack>
                              <CoachPartnershipActions
                                @planner={{@model.planner}}
                                @coachUserId={{coachProfile.user}}
                                @buttonClass="btn btn-sm text-nowrap"
                                @collapseMultipleActions={{true}}
                              />
                            </:actionsBack>
                            <:actionsFront>
                              <LinkTo
                                @route="coaching.coachee.cohort.coaches.coach"
                                @models={{array
                                  @model.program_id
                                  @model.cohort_id
                                  coachProfile.user
                                }}
                                class="btn btn-{{if
                                    icon.color
                                    icon.color
                                    'primary'
                                  }}
                                  btn-sm"
                              >Full profile
                                <iconify-icon
                                  icon="ph:arrow-right-bold"
                                  inline
                                /></LinkTo>
                            </:actionsFront>
                          </CoachCard>
                        </div>
                      {{/let}}
                    {{/let}}
                  {{/let}}
                {{/each}}
              </div>
            {{else}}
              <None />
            {{/if}}
          </:content>
        </Request>
      {{else}}
        <None />
      {{/if}}
    </div>
  </template>
}
